import React, { useState, useRef } from 'react';
import { seaDaysByPerson, qualifiesSeaDay, totalHrs, fmtDate, fmtHrs, isStayEntry, isRaceEntry, nightsAboard, hullNm, annualSummaries } from '../lib/utils';

const routeOf = (e) => {
  const dep = e.departure || 'EBM';
  const dest = e.destination || e.arrival || dep;
  return dep === dest ? `${dep} (round trip)` : `${dep} → ${dest}`;
};
const fmtH = (h) => h == null ? '—' : `${Math.floor(h)}h ${String(Math.round((h % 1) * 60)).padStart(2, '0')}m`;

function PersonCard({ p, open, onToggle }) {
  const isCap = p.captainDays > 0;
  return (
    <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 12, marginBottom: 10, boxShadow: 'var(--sh)', overflow: 'hidden' }}>
      <div onClick={onToggle} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 16px', cursor: 'pointer' }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--navy)' }}>
            {p.person}
            {isCap && <span style={{ fontFamily: "'IBM Plex Mono',monospace", fontSize: 10, fontWeight: 700, letterSpacing: '.08em', color: 'var(--slate)' }}> · Skipper</span>}
          </div>
          <div style={{ fontFamily: "'IBM Plex Mono',monospace", fontSize: 10, color: 'var(--slate)', marginTop: 3 }}>
            {p.captainDays > 0 ? `${p.captainDays} as skipper · ` : ''}{Math.round(p.nm).toLocaleString()} nm logged
          </div>
        </div>
        <div style={{ textAlign: 'right', flexShrink: 0, paddingLeft: 10 }}>
          <div style={{ fontFamily: "'IBM Plex Mono',monospace", fontSize: 24, fontWeight: 800, color: 'var(--gold)', lineHeight: 1 }}>{p.days}</div>
          <div style={{ fontFamily: "'IBM Plex Mono',monospace", fontSize: 8, fontWeight: 700, letterSpacing: '.1em', textTransform: 'uppercase', color: 'var(--slate)', marginTop: 3 }}>Qual. Days</div>
        </div>
      </div>
      {open && (
        <div style={{ borderTop: '1px solid var(--border)', background: 'var(--s2)' }}>
          {[...p.trips].sort((a, b) => new Date(b.date) - new Date(a.date)).map((e, i) => (
            <div key={e.id || i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', padding: '9px 16px', borderBottom: '1px solid var(--border)' }}>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontFamily: "'IBM Plex Mono',monospace", fontSize: 10, color: 'var(--gold-deep, #8B6508)', fontWeight: 700 }}>{(fmtDate(e.date) || '').replace(/^\w+,\s*/, '')}</div>
                <div style={{ fontSize: 13, color: 'var(--ink)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '62vw' }}>{routeOf(e)}</div>
              </div>
              <div style={{ fontFamily: "'IBM Plex Mono',monospace", fontSize: 12, fontWeight: 600, color: 'var(--navy)', textAlign: 'right', flexShrink: 0 }}>
                {fmtH(totalHrs(e))}
                {(e.captain || 'Frederick') === p.person && <div style={{ fontSize: 8, color: 'var(--slate)', letterSpacing: '.08em', textTransform: 'uppercase' }}>Skipper</div>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// A quiet number over a hairline — the design-system stat cell.
function StatCell({ value, label, sub, accent }) {
  return (
    <div style={{ padding: '2px 0' }}>
      <div style={{ fontFamily: "'IBM Plex Mono',monospace", fontSize: 24, fontWeight: 600, color: accent || 'var(--navy)', letterSpacing: '-.01em', lineHeight: 1 }}>{value}</div>
      <div style={{ fontFamily: "'Plus Jakarta Sans',sans-serif", fontSize: 10.5, fontWeight: 600, letterSpacing: '.08em', textTransform: 'uppercase', color: 'var(--slate)', marginTop: 6 }}>{label}</div>
      {sub && <div style={{ fontFamily: "'IBM Plex Mono',monospace", fontSize: 10, color: 'var(--slate)', marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

function StatSection({ title, children }) {
  return (
    <div style={{ background: 'var(--surface)', borderRadius: 16, padding: '18px 20px', marginBottom: 14, boxShadow: '0 1px 2px rgba(11,22,38,.04), 0 8px 24px rgba(11,22,38,.06)' }}>
      <div style={{ fontFamily: "'Plus Jakarta Sans',sans-serif", fontSize: 12, fontWeight: 600, letterSpacing: '.09em', textTransform: 'uppercase', color: 'var(--slate)', marginBottom: 16 }}>{title}</div>
      {children}
    </div>
  );
}

function grid(cells) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(92px, 1fr))', gap: '18px 16px' }}>
      {cells}
    </div>
  );
}

function FleetStats({ entries, settings }) {
  const passages = entries.filter(e => !isStayEntry(e));
  const stays = entries.filter(isStayEntry);
  const races = entries.filter(isRaceEntry);
  const solo = entries.filter(e => e.solo === 'Y');

  const seaHrs = entries.reduce((s, e) => s + (totalHrs(e) || 0), 0);
  const totalNm = hullNm(entries, settings);
  const loggedNm = entries.reduce((s, e) => s + (e.imported ? 0 : (Number(e.nm) || 0)), 0);
  const nights = entries.reduce((s, e) => s + nightsAboard(e), 0);
  const anchorNights = entries.filter(e =>
    e.trip_type === 'At Anchor' || e.trip_type === 'Day at Anchor' ||
    (e.dest_type || '').toLowerCase().includes('anchor') || e.anchoring === 'O'
  ).reduce((s, e) => s + nightsAboard(e), 0);
  const dockNights = stays.filter(e => e.trip_type === 'At Dock').reduce((s, e) => s + nightsAboard(e), 0);
  const qualDays = entries.filter(qualifiesSeaDay).length;

  // Times anchored = anchor stay entries + any passage that anchored (imported
  // history records this on the `anchoring` field: O overnight, D daytime).
  const anchorEvents = entries.filter(e =>
    e.trip_type === 'At Anchor' || e.trip_type === 'Day at Anchor' ||
    e.anchoring === 'O' || e.anchoring === 'D'
  ).length;

  // Longest passage + fastest logged max SOG, for a bit of colour
  const longest = passages.reduce((a, e) => ((Number(e.nm) || 0) > (Number(a?.nm) || 0) ? e : a), null);
  const topSog = entries.reduce((m, e) => Math.max(m, Number(e.max_sog) || 0), 0);

  return (
    <>
      <StatSection title="Logbook at a glance">
        {grid([
          <StatCell key="nm" value={totalNm.toLocaleString()} label="Hull nm" sub={loggedNm > 0 ? `+${Math.round(loggedNm)} logged` : 'baseline'} accent="var(--gold)" />,
          <StatCell key="sea" value={fmtHrs(seaHrs) || '0h'} label="Time at sea" />,
          <StatCell key="pass" value={passages.length} label="Passages" />,
          <StatCell key="qual" value={qualDays} label="Qualifying days" sub="3h+ underway" />,
        ])}
      </StatSection>

      <StatSection title="Nights aboard">
        {grid([
          <StatCell key="tot" value={nights} label="Total nights" />,
          <StatCell key="anc" value={anchorNights} label="At anchor" />,
          <StatCell key="dock" value={dockNights} label="At dock" />,
          <StatCell key="times" value={anchorEvents} label="Times anchored" />,
        ])}
      </StatSection>

      <StatSection title="On the water">
        {grid([
          <StatCell key="race" value={races.length} label="Races" accent={races.length ? 'var(--red)' : undefined} />,
          <StatCell key="solo" value={solo.length} label="Solo trips" />,
          <StatCell key="long" value={longest?.nm ? `${Math.round(longest.nm)}` : '—'} label="Longest leg" sub={longest?.nm ? 'nm' : ''} />,
          <StatCell key="sog" value={topSog ? topSog.toFixed(1) : '—'} label="Top SOG" sub={topSog ? 'kts' : ''} />,
        ])}
      </StatSection>
    </>
  );
}

const A_SANS = "'Plus Jakarta Sans',sans-serif";
const A_MONO = "'IBM Plex Mono',monospace";

// Annual summary: a metric-toggle bar chart that also picks the year, linked to
// swipeable per-year detail cards. Bars tap → cards snap; card scroll → bar syncs.
function AnnualSummary({ entries }) {
  const years = annualSummaries(entries);              // newest first
  const [metric, setMetric] = useState('nm');          // 'nm' | 'hrs' | 'days'
  const [sel, setSel] = useState(years.length ? years[0].year : null);
  const scroller = useRef(null);
  if (!years.length) return null;

  const asc = [...years].reverse();                     // oldest→newest for the bars
  const metricVal = (y) => metric === 'nm' ? y.distance : metric === 'hrs' ? Math.round(y.seaHrs) : y.daysUnderway;
  const metricMax = Math.max(...asc.map(metricVal), 1);
  const metricLabel = metric === 'nm' ? 'Nautical miles / year' : metric === 'hrs' ? 'Hours at sea / year' : 'Days underway / year';
  const unitFor = (v) => metric === 'nm' ? `${v.toLocaleString()}` : metric === 'hrs' ? `${v}h` : `${v}`;

  const selectYear = (yr) => {
    setSel(yr);
    const idx = years.findIndex(y => y.year === yr);    // cards are newest-first
    const el = scroller.current;
    if (el && idx >= 0) { const card = el.children[idx]; if (card) el.scrollTo({ left: card.offsetLeft - 4, behavior: 'smooth' }); }
  };
  const onScroll = () => {
    const el = scroller.current; if (!el) return;
    const mid = el.scrollLeft + el.clientWidth / 2;
    let best = 0, bestD = Infinity;
    for (let i = 0; i < el.children.length; i++) { const c = el.children[i]; const cc = c.offsetLeft + c.offsetWidth / 2; const d = Math.abs(cc - mid); if (d < bestD) { bestD = d; best = i; } }
    const yr = years[best]?.year; if (yr && yr !== sel) setSel(yr);
  };

  return (
    <div style={{ marginBottom: 20 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '2px 4px 10px' }}>
        <span style={{ fontFamily: A_SANS, fontSize: 12, fontWeight: 600, letterSpacing: '.09em', textTransform: 'uppercase', color: 'var(--slate)' }}>Year over year</span>
        <span style={{ flex: 1, height: 1, background: 'var(--hair)' }} />
      </div>

      {/* Bars = compare + picker */}
      <div style={{ background: 'var(--surface)', borderRadius: 16, padding: 18, marginBottom: 12, boxShadow: '0 1px 2px rgba(11,22,38,.04), 0 8px 24px rgba(11,22,38,.06)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <span style={{ fontFamily: A_MONO, fontSize: 11, color: 'var(--slate)' }}>{metricLabel}</span>
          <div style={{ display: 'flex', gap: 4 }}>
            {['nm', 'hrs', 'days'].map(m => (
              <button key={m} onClick={() => setMetric(m)} style={{ fontFamily: A_SANS, fontSize: 10, fontWeight: 600, padding: '5px 9px', borderRadius: 7, border: 'none', background: metric === m ? 'var(--navy)' : 'var(--s2)', color: metric === m ? '#fff' : 'var(--slate)', cursor: 'pointer' }}>{m}</button>
            ))}
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 10, height: 120 }}>
          {asc.map(y => {
            const v = metricVal(y);
            const on = y.year === sel;
            return (
              <div key={y.year} onClick={() => selectYear(y.year)} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-end', height: '100%', cursor: 'pointer' }}>
                <div style={{ fontFamily: A_MONO, fontSize: 10, fontWeight: 600, color: 'var(--navy)', marginBottom: 5 }}>{unitFor(v)}</div>
                <div style={{ width: '100%', maxWidth: 44, height: `${Math.max(4, (v / metricMax) * 100)}%`, background: on ? 'var(--gold)' : 'var(--accent, #6E8799)', borderRadius: '6px 6px 0 0', transition: '.2s' }} />
                <div style={{ fontFamily: A_MONO, fontSize: 10, color: on ? 'var(--navy)' : 'var(--slate)', fontWeight: on ? 700 : 400, marginTop: 7 }}>'{y.year.slice(2)}</div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Swipeable per-year cards */}
      <div ref={scroller} onScroll={onScroll} style={{ display: 'flex', gap: 12, overflowX: 'auto', padding: '2px 4px 10px', scrollSnapType: 'x mandatory', WebkitOverflowScrolling: 'touch' }}>
        {years.map(y => {
          const cur = y.year === sel;
          const rows = [
            ['Time at sea', fmtHrs(y.seaHrs) || null],
            ['Days out', y.daysUnderway || null],
            ['Passages', y.passages || null],
            ['Races', y.races || null],
            y.topMonth && ['Top month', `${y.topMonth.name} · ${y.topMonth.nm}nm`],
            y.engineRun != null && ['Engine (est)', `${y.engineRun}h`],
            y.topSog != null && ['Top SOG', `${y.topSog} kts`],
          ].filter(Boolean).filter(r => r[1] != null);
          const bestYet = years.every(o => o.year === y.year || o.distance <= y.distance) && years.length > 1;
          return (
            <div key={y.year} style={{ flex: 'none', width: 210, scrollSnapAlign: 'center', background: 'var(--surface)', borderRadius: 16, padding: 17, border: cur ? '1px solid var(--gold)' : '1px solid var(--border)', boxShadow: cur ? '0 0 0 3px rgba(184,134,11,.1)' : 'none' }}>
              <div style={{ fontFamily: A_MONO, fontSize: 13, fontWeight: 700, color: 'var(--navy)', display: 'flex', alignItems: 'center' }}>
                {y.year}{bestYet && <span style={{ fontSize: 8.5, color: 'var(--gold-deep, #8B6508)', background: 'rgba(184,134,11,.14)', padding: '2px 6px', borderRadius: 4, marginLeft: 8, letterSpacing: '.05em', textTransform: 'uppercase' }}>best yet</span>}
              </div>
              <div style={{ fontFamily: A_MONO, fontSize: 30, fontWeight: 600, color: cur ? 'var(--gold)' : 'var(--navy)', margin: '10px 0 2px', letterSpacing: '-.01em' }}>{y.distance.toLocaleString()}</div>
              <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '.06em', color: 'var(--slate)', fontWeight: 600 }}>nautical miles</div>
              {y.deltaNm != null && <div style={{ fontFamily: A_MONO, fontSize: 10, color: y.deltaNm >= 0 ? '#3a7d44' : 'var(--red)', marginTop: 4 }}>{y.deltaNm >= 0 ? '+' : ''}{y.deltaNm} vs {(+y.year - 1)}</div>}
              <div style={{ marginTop: 14 }}>
                {rows.map(([k, v]) => (
                  <div key={k} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderTop: '1px solid var(--hair)', fontSize: 12 }}>
                    <span style={{ color: 'var(--slate)' }}>{k}</span>
                    <span style={{ fontFamily: A_MONO, fontWeight: 600, color: 'var(--navy)' }}>{v}</span>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
      <div style={{ display: 'flex', justifyContent: 'center', gap: 6, marginTop: 2 }}>
        {years.map(y => <i key={y.year} style={{ width: y.year === sel ? 16 : 6, height: 6, borderRadius: 3, background: y.year === sel ? 'var(--navy)' : 'var(--border)', transition: '.2s' }} />)}
      </div>
    </div>
  );
}

export default function CrewTab({ entries = [], settings = {} }) {
  const people = seaDaysByPerson(entries);
  const [open, setOpen] = useState(null);

  return (
    <div style={{ paddingBottom: 'calc(88px + env(safe-area-inset-bottom))' }}>
      <AnnualSummary entries={entries} />
      <FleetStats entries={entries} settings={settings} />

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', margin: '2px 4px 12px' }}>
        <span style={{ fontFamily: "'Plus Jakarta Sans',sans-serif", fontSize: 12, fontWeight: 600, letterSpacing: '.09em', textTransform: 'uppercase', color: 'var(--slate)' }}>Crew &amp; Sea Time</span>
        <span style={{ fontFamily: "'IBM Plex Mono',monospace", fontSize: 10, color: 'var(--slate)' }}>Qualifying day = 3h+ underway</span>
      </div>
      {people.length === 0
        ? <div style={{ textAlign: 'center', padding: '48px 20px', color: 'var(--slate)', fontSize: 14 }}>No qualifying sea days logged yet. Trips of 3 hours or more count toward each crew member.</div>
        : people.map(p => <PersonCard key={p.person} p={p} open={open === p.person} onToggle={() => setOpen(open === p.person ? null : p.person)} />)
      }
    </div>
  );
}
